import type {
  BehaviorConnection,
  BehaviorNode,
} from "../blueprint/types.js";

/** Persisted behavior graph attached to fields, actions, or modals. */
export interface CustomDisciplineBehavior {
  version: number;
  graph: {
    nodes: unknown[];
    connections: unknown[];
  };
}

/** Returns an empty custom-discipline behavior graph. */
export const emptyBehavior = (): CustomDisciplineBehavior => ({
  version: 1,
  graph: { nodes: [], connections: [] },
});

/** Coerces a stored behavior payload into typed blueprint nodes and connections. */
export function normalizeBehaviorGraph(
  behavior: CustomDisciplineBehavior | null | undefined,
): {
  nodes: BehaviorNode[];
  connections: BehaviorConnection[];
} {
  const graph = behavior?.graph ?? emptyBehavior().graph;
  return {
    nodes: Array.isArray(graph.nodes) ? (graph.nodes as BehaviorNode[]) : [],
    connections: Array.isArray(graph.connections)
      ? (graph.connections as BehaviorConnection[])
      : [],
  };
}

/** Returns whether a behavior payload is structurally valid. */
export function isValidCustomDisciplineBehavior(
  behavior: unknown,
): behavior is CustomDisciplineBehavior {
  if (behavior === undefined || behavior === null) {
    return true;
  }
  if (typeof behavior !== "object" || behavior === null) {
    return false;
  }
  const candidate = behavior as CustomDisciplineBehavior;
  const graph = candidate.graph;
  return (
    candidate.version !== undefined &&
    typeof graph === "object" &&
    graph !== null &&
    Array.isArray(graph.nodes) &&
    Array.isArray(graph.connections)
  );
}
